import React, { useRef, useState } from "react";
import { Button, Input } from "reactstrap";
import notifications from "../../../common/Components/Notifications/Notification";
import { attachSareeLabel, identityError } from "../../../services/physicalIdentityApi";
import { SalesScanStage, useSalesScanner } from "../../sales/SalesScanPad";
import { useKT } from "../i18n/kt";

type Props = {
  pieceId: string;
  sareeNumber: string;
  /** Serial of the label already on this saree, if any. */
  labelSerial?: string | null;
  onAttached?: (serial?: string) => void;
};

/**
 * The HKP label pasted on the loom when a saree starts (stitched on when it is cut). Scanning it
 * here ties every later scan to this saree's journey; the saree's draft product is made if needed.
 */
export default function SareeLabelAttach({ pieceId, sareeNumber, labelSerial, onAttached }: Props) {
  const { t } = useKT();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);

  const attach = async (raw: string) => {
    if (!raw.trim() || lock.current) return;
    lock.current = true; setBusy(true); setError("");
    try {
      const res = await attachSareeLabel(pieceId, raw);
      notifications({ title: t("Label attached"), message: t("Saree {{n}} now has label {{s}}", { n: sareeNumber, s: res.serial || "" }), type: "success" });
      setOpen(false); setCode("");
      onAttached?.(res.serial);
    } catch (e) {
      setError(identityError(e));
    } finally {
      lock.current = false; setBusy(false);
    }
  };
  const scan = useSalesScanner((value) => { setCode(value); void attach(value); }, busy);

  if (labelSerial) return <span className="kw-chip is-ok" title={t("HKP label on this saree")}>{t("Label")} {labelSerial}</span>;
  if (!open) return <Button size="sm" outline onClick={() => setOpen(true)}>{t("Attach HKP label")}</Button>;
  return <div className="kh-label-attach">
    <p className="kw-muted">{t("Scan the HKP label pasted on the loom for saree {{n}}, or type the code printed under it.", { n: sareeNumber })}</p>
    <div className="identity-inline">
      <Input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void attach(code); }}
        placeholder="HKP-000123" aria-label={t("Label code")} autoComplete="off" autoFocus />
      <Button size="sm" className="ResetBtn" disabled={busy || !code.trim()} onClick={() => void attach(code)}>{busy ? t("Saving…") : t("Attach")}</Button>
      <Button size="sm" outline disabled={busy} onClick={scan.startCamera}>{t("Scan")}</Button>
      <Button size="sm" outline disabled={busy} onClick={() => { setOpen(false); setError(""); }}>{t("Cancel")}</Button>
    </div>
    <input ref={scan.fileRef} type="file" accept="image/*" hidden onChange={(e) => { scan.onFile(e.target.files?.[0]); e.target.value = ""; }} />
    <SalesScanStage scan={scan} />
    {error ? <p role="alert" className="text-danger kw-muted">{error}</p> : null}
  </div>;
}
